import { Fix64 } from "../maths/fix64";
import { EventReward, parseReward } from "./events";

/**
 * The weekly event modes' data (MP-21, docs/MODES.md §5; balance/src/events.json `modes`): Boss Week's tiers, Race
 * Week's roads and medals, the Odyssey's maps, decks and attempts. The rules that read it live in meta/eventModes.ts
 * (the runs) and meta/eventModesState.ts (what a week has paid); a rotation kind whose id is a mode's ("boss", "race",
 * "odyssey") is that mode's week.
 */

/** What a cleared tier, a medal or an Odyssey map pays: a reward, and embers to the week's track. */
export interface ModePrize { readonly embers: number; readonly reward: EventReward }

/** One Boss Week tier: the same fight with the raiders' health, armour and numbers raised. */
export interface BossTierData extends ModePrize {
  readonly hpMult: Fix64;
  /** Added to every raider's armour against physical hits. */
  readonly armour: Fix64;
  /** Every group's count per stage × this (rounded up). */
  readonly countMult: Fix64;
  /** The Elite tier: the week's Weekly Trial modifier on top. */
  readonly elite: boolean;
}

export interface BossWeekData {
  /** The campaign layouts the fight borrows, one a Boss Week in turn (each closes on its signature boss). */
  readonly layouts: readonly number[];
  /** The layout's first `stages` stages, its boss closing the last. */
  readonly stages: number;
  /** Every tier's health × this on top of its own hpMult: tier 1 sits this far under the player's frontier. */
  readonly hpBase: Fix64;
  readonly tiers: readonly BossTierData[];
}

export type Medal = "bronze" | "silver" | "gold";
export const MEDALS: readonly Medal[] = ["bronze", "silver", "gold"];

/** A Race Week road: a layout, its stage count, and the medal times (sim milliseconds, a lower time is better). */
export interface RaceRoadData {
  readonly level: number;
  readonly stages: number;
  readonly goldMs: number;
  readonly silverMs: number;
  readonly bronzeMs: number;
}

export interface RaceWeekData {
  /** One a Race Week, in turn. */
  readonly roads: readonly RaceRoadData[];
  readonly medals: Readonly<Record<Medal, ModePrize>>;
}

/** One Odyssey week: its three maps (layouts) and the deck its runs deal (tower ids). */
export interface OdysseyWeekData { readonly maps: readonly number[]; readonly deck: readonly string[] }

export interface OdysseyData {
  /** Attempts a week; a lost map (the life pool spent) ends one. */
  readonly attempts: number;
  /** Each map's first `stages` stages, its boss closing the last. */
  readonly stages: number;
  /** One an Odyssey, in turn. */
  readonly weeks: readonly OdysseyWeekData[];
  /** What each map pays the first time it is cleared in the week (map 1, 2, 3). */
  readonly maps: readonly ModePrize[];
  /** What clearing all three in one attempt pays, once a week. */
  readonly full: ModePrize;
}

export interface EventModesData {
  readonly boss: BossWeekData;
  readonly race: RaceWeekData;
  readonly odyssey: OdysseyData;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

const MS_PER_SECOND = 1000;

function int(v: Json, what: string): number {
  if (typeof v === "number" && Number.isInteger(v)) return v;
  throw new TypeError(`events.modes: ${what} must be a whole number, got ${JSON.stringify(v)}`);
}
const fix = (v: Json): Fix64 => Fix64.parse(String(v));
const prize = (p: Json, what: string): ModePrize => ({ embers: int(p?.embers ?? 0, `${what}.embers`), reward: parseReward(p?.reward ?? {}) });
const ints = (list: Json, what: string): number[] => (list as Json[]).map((x, i) => int(x, `${what}[${i}]`));

/** Reads events.json `modes`; null when the bundle has none (an old bundle, a test fixture). */
export function parseEventModes(m: Json): EventModesData | null {
  if (m === null || m === undefined) return null;
  const b = m.boss, r = m.race, o = m.odyssey;
  return {
    boss: {
      layouts: ints(b.layouts, "boss.layouts"), stages: int(b.stages, "boss.stages"), hpBase: fix(b.hpBase ?? "1"),
      tiers: (b.tiers as Json[]).map((t, i) => ({ ...prize(t, `boss.tiers[${i}]`), hpMult: fix(t.hp), armour: fix(t.armour ?? "0"), countMult: fix(t.count ?? "1"), elite: t.elite === true })),
    },
    race: {
      roads: (r.roads as Json[]).map((x, i) => ({
        level: int(x.level, `race.roads[${i}].level`), stages: int(x.stages, `race.roads[${i}].stages`),
        goldMs: int(x.gold, "gold") * MS_PER_SECOND, silverMs: int(x.silver, "silver") * MS_PER_SECOND, bronzeMs: int(x.bronze, "bronze") * MS_PER_SECOND,
      })),
      medals: { bronze: prize(r.medals.bronze, "race.bronze"), silver: prize(r.medals.silver, "race.silver"), gold: prize(r.medals.gold, "race.gold") },
    },
    odyssey: {
      attempts: int(o.attempts, "odyssey.attempts"), stages: int(o.stages, "odyssey.stages"),
      weeks: (o.weeks as Json[]).map((w, i) => ({ maps: ints(w.maps, `odyssey.weeks[${i}].maps`), deck: (w.deck as Json[]).map(String) })),
      maps: (o.maps as Json[]).map((p, i) => prize(p, `odyssey.maps[${i}]`)), full: prize(o.full, "odyssey.full"),
    },
  };
}
